import { setSelectedQueryModal } from "redux/SingleDataset/actions";
import { useRef, useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import QueryModal from "views/components/QueryModal";

import {
  QueryOperator,
  FILE_QUERY_OPERATORS,
  FileQueryOperators,
  VqlQuery,
} from "types";
import QueryModalInput from "views/components/QueryModal/QueryModalInput";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import SubmitQueryButton from "views/components/QueryModal/SubmitQueryButton";
import QueryModalOperators from "views/components/QueryModal/QueryModalOperators";
import useVqlParams from "hooks/useVqlParams";
import { FileIcon } from "assets/icons/FileIcon";

import styles from "./style.module.scss";
import {
  createAndUpdateFileFilter,
  isVqlQuery,
  getQueryValuesForFilter,
} from "helpers/utility/vqlHelpers";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { useTextOrMultiQueryModal } from "hooks/UseQueryModal/useTextOrMultiQueryModal";
import { useTreeFolderData } from "./useTreeFolderData";

interface FileQueryModalProps {
  editQueryId?: string;
}

export default function FileQueryModal({ editQueryId }: FileQueryModalProps) {
  const dispatch = useDispatch();
  const {
    addVqlQuery,
    findVqlQuery,
    updateVqlQuery,
    removeVqlQuery,
    vqlQueries,
  } = useVqlParams();
  const { files: filesFromTreeFolder, isLoading } = useTreeFolderData();

  const oldQuery = editQueryId ? findVqlQuery(editQueryId, "file") : null;
  const previousOperator = oldQuery
    ? convertAPIOperator(oldQuery?.file.op)
    : QueryOperator.IS_ONE_OF;
  const [selectedOperator, setSelectedOperator] = useState(previousOperator);
  const existingQuery = useMemo(() => {
    return (
      vqlQueries.find(
        (query: VqlQuery) =>
          isVqlQuery(query, "file") &&
          convertAPIOperator(query.file?.op) === selectedOperator,
      ) || null
    );
  }, [vqlQueries, selectedOperator]);
  const queryValues = useMemo(() => {
    return getQueryValuesForFilter(
      editQueryId,
      oldQuery,
      selectedOperator,
      previousOperator,
      existingQuery,
      "file",
    );
  }, [
    editQueryId,
    oldQuery,
    selectedOperator,
    previousOperator,
    existingQuery,
  ]);

  const previousValues = useMemo(() => {
    return queryValues && queryValues.length > 0
      ? filesFromTreeFolder.map((file) => {
          const isSelected = Array.isArray(queryValues)
            ? queryValues.includes(file.full_path)
            : queryValues === file.full_path;
          return { ...file, selected: isSelected };
        })
      : filesFromTreeFolder;
  }, [filesFromTreeFolder, queryValues]);

  const previousSearchText = queryValues
    ? Array.isArray(queryValues)
      ? ""
      : (queryValues as string)
    : "";

  const [files, setFiles] = useState(previousValues);
  const [searchText, setSearchText] = useState(previousSearchText);
  const modalRef = useRef<HTMLDivElement>(null);

  const selectedMode =
    selectedOperator === QueryOperator.IS_ONE_OF ? "multiple" : "text";

  const selectedFiles = useMemo(
    () => files.filter((file) => file.selected),
    [files],
  );

  useEffect(() => {
    setFiles(previousValues);
    setSearchText(previousSearchText);
    // eslint-disable-next-line
  }, [existingQuery?.id, isLoading]);

  const { isSubmitDisabled } = useTextOrMultiQueryModal({
    mode: selectedMode,
    selectedItems: selectedFiles.map((f) => f.id),
    previousItems: previousValues.filter((f) => f.selected).map((f) => f.id),
    searchText,
    previousText: previousSearchText,
    selectedOperator,
    previousOperator,
    editQueryId,
    existingQueryId: existingQuery?.id,
  });

  const placeholder = selectedMode === "multiple" ? "Search..." : "Text Input";

  const handleRemoveFile = (id: number | string) => {
    const newFiles = files.map((file) => {
      if (file.id === id) {
        return { ...file, selected: false };
      }

      return file;
    });
    setFiles(newFiles);
  };

  const handleSelectFile = (id: number | string) => {
    const newFiles = files.map((file) => {
      if (file.id === id) {
        return { ...file, selected: !file.selected };
      }
      return file;
    });

    setFiles(newFiles);
  };

  const handleOptionSelection = (operator: QueryOperator) => {
    setFiles(previousValues);
    setSelectedOperator(operator);
  };

  const handleInputChange = (value: string) => {
    setSearchText(value);
  };

  const handleSubmission = () => {
    if (isSubmitDisabled) {
      return;
    }

    const values =
      selectedMode === "multiple"
        ? selectedFiles.map((file) => file.full_path)
        : searchText;

    if (editQueryId && values.length === 0) {
      removeVqlQuery(editQueryId);
      dispatch(setSelectedQueryModal(null));
      return;
    }
    if (existingQuery && values.length === 0) {
      removeVqlQuery(existingQuery.id);
      dispatch(setSelectedQueryModal(null));
      return;
    }

    const vqlQuery = createAndUpdateFileFilter(
      values,
      selectedOperator as FileQueryOperators,
      editQueryId || existingQuery?.id,
    );
    editQueryId || existingQuery
      ? updateVqlQuery(vqlQuery)
      : addVqlQuery(vqlQuery);
    dispatch(setSelectedQueryModal(null));
  };

  return (
    <QueryModal modalRef={modalRef}>
      <QueryModalTitle icon={<FileIcon />}>File - Video/Image</QueryModalTitle>
      <div className={styles.filterContent}>
        <QueryModalOperators
          operators={[...FILE_QUERY_OPERATORS]}
          selectedOperator={selectedOperator}
          onSelect={handleOptionSelection}
        />
        <QueryModalInput
          options={files.map((file) => ({
            id: file.id,
            text: file.full_path,
          }))}
          mode={selectedMode}
          searchText={searchText}
          selected={selectedFiles.map((file) => file.id)}
          placeholder={placeholder}
          onRemove={handleRemoveFile}
          onSelect={handleSelectFile}
          onInputChange={handleInputChange}
          isLoading={isLoading}
        />

        <SubmitQueryButton
          isDisabled={isSubmitDisabled}
          onSubmit={handleSubmission}
        />
      </div>
    </QueryModal>
  );
}
